import React, { useState } from 'react';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { Box } from '../../../../../../../../CommonComponents/OPL/Box/Box';
import { InputNumberMask } from '../../../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Radio } from '../../../../../../../../CommonComponents/OPL/Radio/Radio';
import { Text } from '../../../../../../../../CommonComponents/OPL/Text/Text';
import {
  BalanceUnitDTO,
  EmployerLimitTypeDTO,
  EmployerMatchTypeDTO,
} from '../../../../../../api/useAccountsQuery';
import { FormFields, getDefaultContribution } from '../../AccountForm.meta';
import { useAccountFormContext } from '../../context/useAccountFormContext';
import { useContribution } from './useContribution';
import { Button } from '../../../../../../../../CommonComponents/OPL/Button/Button';
import { Select } from '../../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { IconButton } from '../../../../../../../../CommonComponents/OPL/IconButton/IconButton';

const UNIT_OPTIONS = [
  { label: '$', value: BalanceUnitDTO.Currency },
  { label: '%', value: BalanceUnitDTO.Percentage },
];

interface DepositsProps {
  label?: string;
  isPercentageDefaultContribution?: boolean;
}

export const Contributions = ({ label = 'Contribution' }: DepositsProps) => {
  const [hasMaximizeContributionIndex, setHasMaximizeContributionIndex] = useState<number | null>(
    null,
  );

  const formBag = useFormContext<FormFields>();

  const contributionFieldArray = useFieldArray<FormFields, 'bankAccount.Contributions', 'key'>({
    name: 'bankAccount.Contributions',
    keyName: 'key',
    control: formBag.control,
  });

  const { setAlert } = useAccountFormContext();

  const accountType = useWatch({ name: 'bankAccount.AccountType', control: formBag.control });
  const isOrionAccount = useWatch({ name: 'bankAccount.IsOrion', control: formBag.control });
  const isIntegrated = useWatch({ name: 'bankAccount.IsIntegrated', control: formBag.control });
  const syncMonthlyContribution = useWatch({
    name: 'bankAccount.SyncMonthlyContributionEnabled',
    control: formBag.control,
  });
  const watchedContributions = useWatch({
    name: 'bankAccount.Contributions',
    control: formBag.control,
  });

  const isAnnuityQualified = useWatch({
    name: 'bankAccount.AnnuityIsQualified',
    control: formBag.control,
  });

  const {
    contributionPeriodOptions,
    hasContributionPeriodOption,
    canEmployeeMaxContribution,
    hasContributionPercentOption,
    contributionTaxStatusOptions,
    isRothAccount,
    employerMatchOptions,
    hasEmployerMatch,
    employerContributionTaxStatusesOptions,
    employerLimitOptions,
    isSimpleIRA,
  } = useContribution();

  const handleSetSyncAlert = () => {
    if (!syncMonthlyContribution || !isIntegrated || isOrionAccount) {
      return;
    }

    setAlert({
      message:
        'Changing your account contributions will automatically un-sync sync account contributions for this account. To re-sync, toggle the sync account contributions toggle in the sync tab.',
      type: 'warning',
    });

    formBag.setValue('bankAccount.SyncMonthlyContributionEnabled', false);
  };

  return (
    <Box>
      <Text variant="formLabel" color="neutral.600">
        {label}
      </Text>
      <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
        {contributionFieldArray.fields.map((field, index) => {
          const {
            PersonalMaximize: maximizeContributions,
            PersonalUnit: personalUnit,
            EmployerMatchType: employerMatchSelection,
            EmployerMultiTierMatch: multiTierMatchList,
            EmployerLimitType: employerLimitType,
          } = watchedContributions[index] || getDefaultContribution(accountType);

          return (
            <React.Fragment key={field.key}>
              {hasContributionPeriodOption && (
                <Box display="flex" alignItems="center" mt={index === 0 ? '0' : '8'}>
                  <Controller
                    name={`bankAccount.Contributions.${index}.PersonalFrequency`}
                    control={formBag.control}
                    render={({ field: { value, name, onChange } }) => {
                      return (
                        <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
                          <legend className="sr-only">Contribution frequency</legend>
                          {contributionPeriodOptions.map(option => (
                            <Radio
                              key={option.value}
                              name={name}
                              checked={!maximizeContributions && value === option.value}
                              onChange={() => {
                                setHasMaximizeContributionIndex(null);

                                formBag.setValue(
                                  `bankAccount.Contributions.${index}.PersonalMaximize`,
                                  false,
                                );

                                formBag.setValue(
                                  `bankAccount.Contributions.${index}.PersonalAmount`,
                                  '',
                                );

                                onChange(option.value);

                                handleSetSyncAlert();
                              }}
                              mr="4"
                            >
                              {option.label}
                            </Radio>
                          ))}

                          {(isAnnuityQualified || canEmployeeMaxContribution) &&
                            (hasMaximizeContributionIndex === null ||
                              hasMaximizeContributionIndex === index) && (
                              <Radio
                                name={name}
                                checked={maximizeContributions}
                                onChange={({ target: { checked } }) => {
                                  setHasMaximizeContributionIndex(index);

                                  formBag.setValue(
                                    `bankAccount.Contributions.${index}.PersonalMaximize`,
                                    checked,
                                  );

                                  formBag.setValue(
                                    `bankAccount.Contributions.${index}.PersonalAmount`,
                                    '',
                                  );

                                  handleSetSyncAlert();
                                }}
                              >
                                Maximize
                              </Radio>
                            )}
                        </fieldset>
                      );
                    }}
                  />
                  {isRothAccount && (
                    <Box pl="8" display="inline-flex">
                      {index === 0 ? (
                        <Button
                          leftIcon="far fa-plus-circle"
                          onClick={() => {
                            contributionFieldArray.append({
                              ...getDefaultContribution(accountType),
                            });
                          }}
                          variant="link"
                        >
                          Add Contribution
                        </Button>
                      ) : (
                        <Button
                          leftIcon="far fa-minus-circle"
                          variantColor="error"
                          onClick={() => {
                            contributionFieldArray.remove(index);
                          }}
                          variant="link"
                        >
                          Remove Contribution
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              <Controller
                control={formBag.control}
                name={`bankAccount.Contributions.${index}.PersonalAmount`}
                render={({ field: personalAmountField }) => (
                  <InputNumberMask
                    aria-label="Contribution amount"
                    {...personalAmountField}
                    value={maximizeContributions ? '' : personalAmountField.value}
                    disabled={maximizeContributions}
                    leftIcon={hasContributionPercentOption ? undefined : 'fa fa-dollar-sign'}
                    allowNegative={false}
                    onChange={evt => {
                      personalAmountField.onChange(evt);

                      handleSetSyncAlert();
                    }}
                    selectorProps={
                      hasContributionPercentOption && !maximizeContributions
                        ? {
                            defaultValue:
                              UNIT_OPTIONS.find(opt => opt.value === personalUnit) ??
                              UNIT_OPTIONS[0],
                            options: UNIT_OPTIONS,
                            onChange(opt) {
                              formBag.setValue(
                                `bankAccount.Contributions.${index}.PersonalUnit`,
                                opt.value,
                              );

                              formBag.setValue(
                                `bankAccount.Contributions.${index}.PersonalAmount`,
                                '',
                              );

                              handleSetSyncAlert();
                            },
                            ariaLabel: 'Contribution Unit',
                          }
                        : undefined
                    }
                  />
                )}
              />

              {isRothAccount && (
                <Controller
                  control={formBag.control}
                  name={`bankAccount.Contributions.${index}.PersonalTaxStatus`}
                  render={({ field: { value, onChange, ref } }) => (
                    <Select
                      label="Contribution Status"
                      innerRef={ref}
                      value={contributionTaxStatusOptions.find(option => option.value === value)}
                      options={contributionTaxStatusOptions}
                      onChange={opt => onChange(opt?.value)}
                      isPortal
                    />
                  )}
                />
              )}

              {hasEmployerMatch && (
                <Controller
                  control={formBag.control}
                  name={`bankAccount.Contributions.${index}.EmployerMatchType`}
                  render={({ field: { value, ref } }) => (
                    <Select
                      tooltip="If eligible, your employer will match this percentage of your contribution. Check with your employer."
                      label="Employer Match"
                      innerRef={ref}
                      value={employerMatchOptions.find(option => option.value === value)}
                      options={employerMatchOptions}
                      isPortal
                      onChange={opt => {
                        if (opt !== null) {
                          formBag.setValue(
                            `bankAccount.Contributions.${index}.EmployerMatchType`,
                            opt.value,
                          );

                          if (opt.value === EmployerMatchTypeDTO.MultiTier) {
                            formBag.setValue(
                              `bankAccount.Contributions.${index}.EmployerMultiTierMatch`,
                              [{ Limit: '', Match: '' }],
                            );
                          }
                        }
                      }}
                    />
                  )}
                />
              )}

              {employerMatchSelection !== null &&
                [
                  EmployerMatchTypeDTO.PercentageOfCompensation,
                  EmployerMatchTypeDTO.PercentOther,
                  EmployerMatchTypeDTO.NonElective,
                ].includes(employerMatchSelection) && (
                  <Controller
                    control={formBag.control}
                    name={`bankAccount.Contributions.${index}.EmployerMatchPercent`}
                    render={({ field: employerMatchPercent }) => (
                      <InputNumberMask
                        {...employerMatchPercent}
                        label={
                          employerMatchSelection === EmployerMatchTypeDTO.NonElective
                            ? 'Employer Contribution Percentage'
                            : 'Match Percentage'
                        }
                        rightIcon="far fa-percent"
                        allowNegative={false}
                      />
                    )}
                  />
                )}

              {employerMatchSelection === EmployerMatchTypeDTO.MultiTier && multiTierMatchList && (
                <Box
                  p="16"
                  display="flex"
                  flexDirection="column"
                  borderRadius="medium"
                  bg="neutral.100"
                  style={{ gap: 16 }}
                >
                  {multiTierMatchList.map((tierItem, multiTierIndex) => {
                    return (
                      <Box
                        key={multiTierIndex}
                        display="grid"
                        style={{
                          gridTemplateColumns: '1fr 1fr auto',
                          alignItems: 'center',
                          gap: 16,
                        }}
                      >
                        <Controller
                          control={formBag.control}
                          name={`bankAccount.Contributions.${index}.EmployerMultiTierMatch.${multiTierIndex}.Limit`}
                          render={({ field: limitField }) => (
                            <InputNumberMask
                              {...limitField}
                              label={multiTierIndex === 0 ? 'First' : 'Next'}
                              rightIcon="far fa-percent"
                              decimalScale={2}
                              allowNegative={false}
                              thousandSeparator={false}
                              style={{ flex: 1 }}
                            />
                          )}
                        />

                        <Controller
                          control={formBag.control}
                          name={`bankAccount.Contributions.${index}.EmployerMultiTierMatch.${multiTierIndex}.Match`}
                          render={({ field: matchField }) => (
                            <InputNumberMask
                              {...matchField}
                              label="Matched at"
                              rightIcon="far fa-percent"
                              decimalScale={2}
                              allowNegative={false}
                              thousandSeparator={false}
                              style={{ flex: 1 }}
                            />
                          )}
                        />

                        <IconButton
                          variant="subtleInactive"
                          icon="far fa-trash-alt"
                          aria-label="Remove Tier"
                          style={{ alignSelf: 'flex-end' }}
                          onClick={() => {
                            const updatedMultiTierMatchList = multiTierMatchList.filter(
                              (_, tierIndex) => {
                                return tierIndex !== multiTierIndex;
                              },
                            );

                            if (updatedMultiTierMatchList.length === 0) {
                              return formBag.setValue(
                                `bankAccount.Contributions.${index}.EmployerMultiTierMatch`,
                                [{ Limit: '', Match: '' }],
                              );
                            }

                            return formBag.setValue(
                              `bankAccount.Contributions.${index}.EmployerMultiTierMatch`,
                              updatedMultiTierMatchList,
                            );
                          }}
                        />
                      </Box>
                    );
                  })}

                  <Button
                    variant="link"
                    leftIcon="far fa-plus-circle"
                    css="align-self: start"
                    onClick={() => {
                      formBag.setValue(
                        `bankAccount.Contributions.${index}.EmployerMultiTierMatch`,
                        [...multiTierMatchList, { Limit: '', Match: '' }],
                      );
                    }}
                  >
                    Add an Addition Match
                  </Button>
                </Box>
              )}

              {employerMatchSelection !== null &&
                [
                  EmployerMatchTypeDTO.Percent50,
                  EmployerMatchTypeDTO.Percent100,
                  EmployerMatchTypeDTO.PercentOther,
                ].includes(employerMatchSelection) && (
                  <Controller
                    control={formBag.control}
                    name={`bankAccount.Contributions.${index}.EmployerLimitType`}
                    render={({ field: { value, ref } }) => (
                      <Select
                        label="Match Limit"
                        innerRef={ref}
                        value={employerLimitOptions.find(option => option.value === value)}
                        options={employerLimitOptions}
                        isPortal
                        onChange={opt => {
                          if (opt !== null) {
                            formBag.setValue(
                              `bankAccount.Contributions.${index}.EmployerLimitType`,
                              opt.value,
                            );
                          }
                        }}
                      />
                    )}
                  />
                )}

              {employerLimitType === EmployerLimitTypeDTO.Other && (
                <Controller
                  control={formBag.control}
                  name={`bankAccount.Contributions.${index}.EmployerEmployerLimit`}
                  render={({ field: employerEmployerLimitField }) => (
                    <InputNumberMask
                      {...employerEmployerLimitField}
                      label="Employer Limit Percentage"
                      rightIcon="far fa-percent"
                      allowNegative={false}
                    />
                  )}
                />
              )}

              {!isSimpleIRA &&
                isRothAccount &&
                employerMatchSelection !== null &&
                ![EmployerMatchTypeDTO.DontKnow, EmployerMatchTypeDTO.NoMatch].includes(
                  employerMatchSelection,
                ) && (
                  <Controller
                    control={formBag.control}
                    name={`bankAccount.Contributions.${index}.EmployerTaxStatus`}
                    render={({ field: { value, onChange, ref } }) => (
                      <Select
                        label="Employer Contribution Status"
                        innerRef={ref}
                        value={employerContributionTaxStatusesOptions.find(
                          option => option.value === value,
                        )}
                        options={employerContributionTaxStatusesOptions}
                        onChange={opt => onChange(opt?.value)}
                        isPortal
                      />
                    )}
                  />
                )}
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
};
